import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DragIndicator,
  VerticalAlignTop,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
} from "@mui/icons-material";
import styles from "./CustomizableWorkspace.module.css";

const WIDTH_STEPS = [4, 6, 8, 12];
const WIDTH_LABELS = { 4: "1/3", 6: "1/2", 8: "2/3", 12: "Full" };

const POSITIONS = ["top", "left", "middle", "right"];
const POSITION_LABELS = {
  top: "Top",
  left: "Left",
  middle: "Middle",
  right: "Right",
};
const POSITION_ICONS = {
  top: VerticalAlignTop,
  left: FormatAlignLeft,
  middle: FormatAlignCenter,
  right: FormatAlignRight,
};

// Builds a small visual thumbnail of a preset layout by reading each
// widget's dock/span, so the "View" options read like a diagram.
export const PresetPreview = ({ layout }) => {
  const entries = Object.entries(layout || {})
    .map(([key, meta]) => ({ key, ...meta }))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const top = entries.filter((e) => e.dock === "top");
  const left = entries.filter((e) => e.dock === "left");
  const middle = entries.filter((e) => e.dock === "middle");
  const right = entries.filter((e) => e.dock === "right");
  const hasRow = left.length || middle.length || right.length;

  const cell = (e) => (
    <span
      key={e.key}
      className={styles.pvCell}
      style={{ flexGrow: e.span || 12 }}
    />
  );

  return (
    <span className={styles.pvBoard} aria-hidden="true">
      {top.map((e) => (
        <span key={e.key} className={styles.pvRow}>
          {cell(e)}
        </span>
      ))}
      {hasRow ? (
        <span className={styles.pvRow}>
          {left.map(cell)}
          {middle.map(cell)}
          {right.map(cell)}
        </span>
      ) : null}
    </span>
  );
};

const loadLayout = (storageKey) => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(storageKey);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveLayout = (storageKey, layout) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(layout));
  } catch {
    /* ignore persistence errors */
  }
};

/**
 * Generic drag-and-drop widget board.
 * widgets: [{ key, title, node, visible, defaultSpan }]
 * Users can reorder (drag) and resize (span) widgets in edit mode.
 * Layout is persisted per storageKey.
 */
const CustomizableWorkspace = ({ storageKey, widgets, presets = [], actions = null, summary = null }) => {
  const [layout, setLayout] = useState(() => loadLayout(storageKey) || {});
  const dragKey = useRef(null);
  const [dragOverKey, setDragOverKey] = useState(null);

  useEffect(() => {
    saveLayout(storageKey, layout);
  }, [storageKey, layout]);

  const defaults = useMemo(() => {
    const map = {};
    widgets.forEach((w, i) => {
      map[w.key] = {
        order: i,
        span: w.defaultSpan || 12,
        dock: w.defaultDock || "top",
      };
    });
    return map;
  }, [widgets]);

  const getMeta = useCallback(
    (key) =>
      layout[key] || defaults[key] || { order: 0, span: 12, dock: "top" },
    [layout, defaults],
  );

  const orderedWidgets = useMemo(() => {
    return widgets
      .filter((w) => w.visible !== false && w.node)
      .sort((a, b) => getMeta(a.key).order - getMeta(b.key).order);
  }, [widgets, getMeta]);

  const topWidgets = orderedWidgets.filter((w) => getMeta(w.key).dock === "top");
  const leftWidgets = orderedWidgets.filter(
    (w) => getMeta(w.key).dock === "left",
  );
  const middleWidgets = orderedWidgets.filter(
    (w) => getMeta(w.key).dock === "middle",
  );
  const rightWidgets = orderedWidgets.filter(
    (w) => getMeta(w.key).dock === "right",
  );
  const leftBasis = leftWidgets[0]
    ? (getMeta(leftWidgets[0].key).span / 12) * 100
    : 33;
  const rightBasis = rightWidgets[0]
    ? (getMeta(rightWidgets[0].key).span / 12) * 100
    : 33;

  const commitDrop = (sourceKey, targetKey) => {
    if (!sourceKey || !targetKey || sourceKey === targetKey) return;
    const targetDock = getMeta(targetKey).dock;
    const current = [...orderedWidgets];
    const from = current.findIndex((w) => w.key === sourceKey);
    const to = current.findIndex((w) => w.key === targetKey);
    if (from === -1 || to === -1) return;
    const [moved] = current.splice(from, 1);
    current.splice(to, 0, moved);
    setLayout((prev) => {
      const next = { ...prev };
      current.forEach((w, idx) => {
        const meta = getMeta(w.key);
        next[w.key] = {
          ...meta,
          order: idx,
          dock: w.key === sourceKey ? targetDock : meta.dock,
        };
      });
      return next;
    });
  };

  const cycleSpan = (key) => {
    const meta = getMeta(key);
    const idx = WIDTH_STEPS.indexOf(meta.span);
    const nextSpan = WIDTH_STEPS[(idx + 1) % WIDTH_STEPS.length];
    setLayout((prev) => ({ ...prev, [key]: { ...meta, span: nextSpan } }));
  };

  const cyclePosition = (key) => {
    const meta = getMeta(key);
    const idx = POSITIONS.indexOf(meta.dock);
    const nextDock = POSITIONS[(idx + 1) % POSITIONS.length];
    setLayout((prev) => ({ ...prev, [key]: { ...meta, dock: nextDock } }));
  };

  const resetLayout = () => setLayout({});

  const applyPreset = (preset) => setLayout({ ...preset.layout });

  // Expose presets + layout actions to the NavBar's "View" dropdown via
  // window events, so the control can live in the header to save space.
  useEffect(() => {
    const detailPresets = presets.map((p) => ({
      key: p.key,
      label: p.label,
      layout: p.layout,
    }));
    window.__posWorkspacePresets = detailPresets;
    window.dispatchEvent(
      new CustomEvent("pos:workspace-presets", {
        detail: { presets: detailPresets },
      }),
    );

    const onApply = (e) => {
      const preset = presets.find((p) => p.key === e.detail?.key);
      if (preset) applyPreset(preset);
    };
    const onReset = () => resetLayout();

    window.addEventListener("pos:apply-preset", onApply);
    window.addEventListener("pos:reset-layout", onReset);
    return () => {
      window.removeEventListener("pos:apply-preset", onApply);
      window.removeEventListener("pos:reset-layout", onReset);
      window.__posWorkspacePresets = null;
      window.dispatchEvent(
        new CustomEvent("pos:workspace-presets", { detail: { presets: [] } }),
      );
    };
  }, [presets]);

  const renderWidget = (w) => {
    const meta = getMeta(w.key);
    const isDragOver = dragOverKey === w.key;
    const PositionIcon = POSITION_ICONS[meta.dock];
    return (
      <div
        key={w.key}
        className={`${styles.widget} ${isDragOver ? styles.dragOver : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOverKey(w.key);
        }}
        onDragLeave={() => setDragOverKey(null)}
        onDrop={(e) => {
          e.preventDefault();
          commitDrop(dragKey.current, w.key);
          dragKey.current = null;
          setDragOverKey(null);
        }}
      >
        <div
          className={styles.widgetBar}
          draggable
          onDragStart={() => {
            dragKey.current = w.key;
          }}
          onDragEnd={() => {
            dragKey.current = null;
            setDragOverKey(null);
          }}
        >
          <span className={styles.widgetHandle}>
            <DragIndicator sx={{ fontSize: 18 }} />
            {w.title}
            <span className={styles.dragTag}>drag to move</span>
          </span>
          <span className={styles.widgetControls}>
            <button
              type="button"
              className={styles.posChip}
              onClick={() => cyclePosition(w.key)}
              title="Place at Top / Left / Middle / Right"
            >
              <PositionIcon sx={{ fontSize: 15 }} />
              {POSITION_LABELS[meta.dock]}
            </button>
            {meta.dock !== "top" && (
              <button
                type="button"
                className={styles.sizeChip}
                onClick={() => cycleSpan(w.key)}
                title="Click to resize"
              >
                {WIDTH_LABELS[meta.span]}
              </button>
            )}
          </span>
        </div>
        <div className={styles.widgetBody}>{w.node}</div>
      </div>
    );
  };

  return (
    <div className={styles.workspace}>
      <div className={styles.toolbar}>
        {summary}
        <div className={styles.toolbarRight}>
          {actions}
        </div>
      </div>

      <div className={styles.stack}>
        {topWidgets.map(renderWidget)}

        {(leftWidgets.length > 0 ||
          middleWidgets.length > 0 ||
          rightWidgets.length > 0) && (
          <div className={styles.splitRow}>
            {leftWidgets.length > 0 && (
              <div
                className={styles.column}
                style={{
                  flex: middleWidgets.length
                    ? `0 0 ${leftBasis}%`
                    : rightWidgets.length
                      ? `0 0 ${leftBasis}%`
                      : "1 1 100%",
                }}
              >
                {leftWidgets.map(renderWidget)}
              </div>
            )}
            {middleWidgets.length > 0 && (
              <div className={styles.column} style={{ flex: "1 1 0" }}>
                {middleWidgets.map(renderWidget)}
              </div>
            )}
            {rightWidgets.length > 0 && (
              <div
                className={styles.column}
                style={{
                  flex: middleWidgets.length
                    ? `0 0 ${rightBasis}%`
                    : "1 1 0",
                }}
              >
                {rightWidgets.map(renderWidget)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomizableWorkspace;
